import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Always use HTTP for API requests
const API_BASE_URL = "https://didrouteapi.com/api/users";

// Add debug logging
// console.log('API Configuration:', {
//   hostname: window.location.hostname,
//   apiUrl: API_BASE_URL,
//   protocol: 'http:', // Force HTTP
//   isProduction: window.location.hostname === 'didroute.com'
// });

export default function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter email first');
      return;
    }

    try {
      console.log('Sending request to:', `${API_BASE_URL}/register`);
      
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        mode: 'cors',
        body: JSON.stringify({
          email: formData.email.trim()
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setShowOTPInput(true);
        setError('');
        setMessage('OTP has been sent to your email');
      } else {
        // Handle specific error for already registered email
        if (data.error && data.error.includes('already registered')) {
          setError('This email is already registered. Please login or use a different email.');
        } else {
          throw new Error(data.error || 'Failed to send OTP');
        }
      }
    } catch (error) {
      console.error('API Error:', {
        url: `${API_BASE_URL}/register`,
        error: error.message,
        hostname: window.location.hostname
      });
      
      if (error.message.includes('Failed to fetch')) {
        setError('Unable to connect to server. Please try again later.');
      } else {
        setError(`Server error: ${error.message}`);
      }
    }
  };

  const handleOTPVerification = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          otp: otp,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsEmailVerified(true);
        setShowOTPInput(false);
        setMessage('Email verified successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(data.error || 'Invalid OTP');
      }
    } catch (error) {
      console.error('OTP Verification Error:', error);
      setError('Verification failed. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isEmailVerified) {
      setError('Please verify your email first');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      
      const response = await fetch(`${API_BASE_URL}/complete-registration`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: fullName,
          address: formData.address,
          email: formData.email,
          phone: formData.phone,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Registration successful! Redirecting to login...');
        setFormData({
          firstName: '',
          lastName: '',
          address: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        throw new Error(data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration Error:', error);
      setError('Registration failed. Please try again or contact support.');
    }
  };

  return (
    <div className="mt-[10px]">
      <div className="flex items-center justify-center min-h-screen bg-cover bg-center relative"
        style={{
          backgroundImage: "url(https://images.pexels.com/photos/2150/sky-space-dark-galaxy.jpg?auto=compress&cs=tinysrgb&w=1600)",
        }}>
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 bg-white bg-opacity-5 backdrop-blur-md m-4 shadow-lg rounded-lg max-w-2xl w-full px-6 py-8 sm:px-10 sm:py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Create your account</h2>
            {message && <div className="mb-4 text-green-400 text-sm">{message}</div>}
            {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300">
                  First Name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-black"
                  placeholder="Enter your first name"
                />
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300">
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-black"
                  placeholder="Enter your last name"
                />
              </div>

              {/* Email with Verify Button */}
              <div className="col-span-1 sm:col-span-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email address
                </label>
                <div className="mt-1 flex">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full rounded-l-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-black"
                    placeholder="Enter your email"
                    disabled={isEmailVerified}
                  />
                  <button
                    type="button"
                    onClick={handleVerifyEmail}
                    disabled={isEmailVerified}
                    className={`px-4 py-2 rounded-r-md text-sm font-medium text-white 
                      ${isEmailVerified 
                        ? 'bg-green-500 cursor-not-allowed' 
                        : 'bg-indigo-500 hover:bg-indigo-400'}`}
                  >
                    {isEmailVerified ? 'Verified' : 'Verify'}
                  </button>
                </div>
              </div>

              {/* OTP Input (Conditional Render) */}
              {showOTPInput && (
                <div className="col-span-1 sm:col-span-2">
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-300">
                    Enter OTP
                  </label>
                  <div className="mt-1 flex">
                    <input
                      id="otp"
                      type="text"
                      maxLength="6"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="block w-full rounded-l-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-black"
                      placeholder="Enter 6-digit OTP"
                      required
                    />
                    <button
                      type="button"
                      onClick={handleOTPVerification}
                      className="px-4 py-2 rounded-r-md bg-indigo-500 text-sm font-medium text-white hover:bg-indigo-400"
                    >
                      Verify OTP
                    </button>
                  </div>
                </div>
              )}

              {/* Rest of the form fields */}
              <div className="col-span-1 sm:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-300">
                  Address
                </label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  required
                  value={formData.address}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-black"
                  placeholder="Enter your address"
                />
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
                  Phone Number
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-black"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-black"
                  placeholder="Enter your password"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 bg-opacity-30 px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-black"
                  placeholder="Confirm your password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={!isEmailVerified}
                className={`w-full rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm transition-transform duration-200 ease-in-out transform hover:scale-105
                  ${!isEmailVerified 
                    ? 'bg-gray-500 cursor-not-allowed' 
                    : 'bg-indigo-500 hover:bg-indigo-400'}`}
              >
                Register
              </button>
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-gray-400">
            Already have an account?{" "}
            <a href="/login" className="font-semibold text-indigo-400 hover:text-indigo-300">
              Sign in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
